import React, { useState } from 'react';
import { Box, Typography, Button, Stack, TextField, Paper } from '@mui/material';
import { motion } from 'framer-motion';
import { useSpring, animated } from 'react-spring';
import SendIcon from '@mui/icons-material/Send';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import LanguageIcon from '@mui/icons-material/Language';
import MessageIcon from '@mui/icons-material/Message';

const CTABanner: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    domain: '',
    message: '',
  });

  const containerAnimation = useSpring({
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
    config: { tension: 280, friction: 20 }
  });

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  return (
    <Box 
      sx={{ 
        py: { xs: 6, md: 10 }, 
        background: 'linear-gradient(135deg, #1158A6 0%, #1e90ff 100%)', 
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          background: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.2) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.2) 0%, transparent 50%)',
          animation: 'pulse 8s ease-in-out infinite',
          '@keyframes pulse': {
            '0%': { opacity: 0.1 },
            '50%': { opacity: 0.2 },
            '100%': { opacity: 0.1 },
          },
        }}
      />

      <animated.div style={containerAnimation}>
        <Stack 
          direction={{ xs: 'column', md: 'row' }} 
          spacing={6} 
          alignItems="center" 
          justifyContent="center" 
          maxWidth="lg" 
          mx="auto"
          px={2}
        >
          {/* Left: Headline and subtext */}
          <Box flex={1} minWidth={260} mb={{ xs: 4, md: 0 }}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Typography 
                variant="h3" 
                fontWeight={800} 
                mb={2}
                sx={{ 
                  background: 'linear-gradient(45deg, #fff 30%, #e6e6e6 90%)',
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Ready to protect your domain reputation?
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.92, mb: 3 }}>
                Start your free audit and see how ImpactQuill can help you reach the inbox, not the spam folder.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon sx={{ color: '#4ECDC4' }} />
                  <Typography variant="body2">Free Domain Audit</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon sx={{ color: '#4ECDC4' }} />
                  <Typography variant="body2">Expert Consultation</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon sx={{ color: '#4ECDC4' }} />
                  <Typography variant="body2">Custom Solutions</Typography>
                </Box>
              </Box>
            </motion.div>
          </Box>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Paper 
              elevation={3} 
              sx={{ 
                p: 4, 
                borderRadius: 4, 
                minWidth: 320, 
                maxWidth: 400, 
                width: '100%', 
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                color: '#1158A6',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              }}
            >
              <Stack 
                spacing={2} 
                component="form" 
                onSubmit={handleSubmit}
                noValidate 
                autoComplete="off"
              >
                <TextField
                  label="Name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  InputProps={{
                    startAdornment: <PersonIcon sx={{ color: '#1158A6', mr: 1 }} />,
                    sx: { borderRadius: 2 }
                  }}
                />
                <TextField
                  label="Email"
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ color: '#1158A6', mr: 1 }} />,
                    sx: { borderRadius: 2 }
                  }}
                />
                <TextField
                  label="Domain"
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                  value={formData.domain}
                  onChange={handleInputChange('domain')}
                  InputProps={{
                    startAdornment: <LanguageIcon sx={{ color: '#1158A6', mr: 1 }} />,
                    sx: { borderRadius: 2 }
                  }}
                />
                <TextField
                  label="Message / Goal"
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  value={formData.message}
                  onChange={handleInputChange('message')}
                  InputProps={{
                    startAdornment: <MessageIcon sx={{ color: '#1158A6', mr: 1, mt: 1 }} />,
                    sx: { borderRadius: 2 }
                  }}
                />
                <Button 
                  variant="contained" 
                  color="primary" 
                  size="large"
                  type="submit"
                  endIcon={<SendIcon />}
                  sx={{ 
                    mt: 1,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600,
                    background: 'linear-gradient(45deg, #1158A6 30%, #1e90ff 90%)',
                    boxShadow: '0 4px 14px rgba(17, 88, 166, 0.2)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(17, 88, 166, 0.3)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Start My Audit Now
                </Button>
              </Stack>
            </Paper>
          </motion.div>
        </Stack>
      </animated.div>
    </Box>
  );
};

export default CTABanner; 